import { yupResolver } from '@hookform/resolvers/yup';
import type { Client } from '@src/clients/domain/Client';
import type { ClientUser } from '@src/clientUser/manage/ClientUser';
import {
  ClientUserNameField,
  ClientUserPhoneField,
} from '@src/clientUser/manage/ClientUserFormInputs';
import { ClientUsersClient } from '@src/clientUser/manage/ClientUsersClient';
import { EditClientUserFormInputs } from '@src/clientUser/manage/update/EditClientUserFormInputs';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

export const useEditClientUser = ({
  client,
  clientUser,
  onError,
  onSuccess,
}: {
  client: Client;
  clientUser?: ClientUser;
  onError: () => void;
  onSuccess: () => void;
}) => {
  const {
    register,
    formState: { errors },
    getValues,
    handleSubmit,
    reset,
  } = useForm({
    resolver: yupResolver(EditClientUserFormInputs),
    defaultValues: {
      [ClientUserNameField.fieldName]: clientUser?.name,
      [ClientUserPhoneField.fieldName]: clientUser?.phone,
    },
  });

  const editClientUser = async () => {
    if (!clientUser) {
      return;
    }

    return await ClientUsersClient.edit(clientUser.id, {
      clientId: client.id,
      name: getValues(ClientUserNameField.fieldName)!,
      phone: getValues(ClientUserPhoneField.fieldName)!,
    });
  };

  const { mutate, isPending } = useMutation({
    mutationFn: editClientUser,
    onError,
    onSuccess,
  });

  return {
    errors,
    register,
    submit: handleSubmit(() => mutate()),
    reset,
    isLoading: isPending,
  };
};
